<template>
  <fieldset class="entes">
    <legend>Quais publicações você quer receber?</legend>

    <p class="carregando" v-if="carregando">Carregando entes federativos...</p>
    <p class="erro" v-else-if="erro">
      Não foi possível carregar a lista de municípios. Você ainda pode se cadastrar
      para receber a legislação estadual.
    </p>

    <template v-else>
      <label class="opcao">
        <input type="checkbox" v-model="receberEstadual" />
        <span>
          Publicações estaduais
          <small>{{ nomeEstadual }}</small>
        </span>
      </label>

      <label class="opcao" v-if="municipios.length > 0">
        <input type="checkbox" v-model="receberMunicipais" />
        <span>Publicações municipais</span>
      </label>

      <div class="municipios" v-if="receberMunicipais">
        <label class="opcao">
          <input type="checkbox" v-model="todos" />
          <span>
            Todos os municípios disponíveis no Legisla
            <small>inclui os municípios publicados aqui no futuro</small>
          </span>
        </label>

        <template v-if="!todos">
          <input
            v-if="mostrarBusca"
            class="busca"
            type="search"
            v-model="filtro"
            :placeholder="`Buscar entre ${municipios.length} municípios`"
          />

          <p class="contador" v-if="selecionados.length > 0">
            {{ selecionados.length }} município(s) selecionado(s)
            <button type="button" @click="selecionados = []">limpar</button>
          </p>

          <ul class="lista">
            <li v-for="municipio in municipiosFiltrados" :key="municipio.id">
              <label class="opcao">
                <input type="checkbox" :value="municipio.id" v-model="selecionados" />
                <span>{{ municipio.nome }}</span>
              </label>
            </li>
          </ul>

          <p class="aviso" v-if="!municipiosValidos">
            Selecione ao menos um município ou desmarque o recebimento municipal.
          </p>
        </template>
      </div>

      <p class="aviso" v-if="!receberEstadual && !receberMunicipais">
        Selecione ao menos uma opção de publicações para receber.
      </p>
    </template>
  </fieldset>
</template>

<script>
import EntesFederativosApi from "../services/EntesFederativosApi";

export default {
  name: "SelecaoEntesFederativos",
  props: {
    // v-model: { receberEstadual, todosMunicipios, entesFederativosIds }
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      municipios: [],
      nomeEstadual: "Legislação Estadual de Goiás",
      filtro: "",
      receberEstadual: true,
      receberMunicipais: false,
      todos: false,
      selecionados: [],
      carregando: true,
      erro: false
    };
  },
  computed: {
    // Hoje são 2 municípios: a busca só aparece quando a lista cresce.
    mostrarBusca() {
      return this.municipios.length > 12;
    },
    municipiosFiltrados() {
      if (!this.filtro) return this.municipios;
      const termo = this.normalizar(this.filtro);
      return this.municipios.filter(m => this.normalizar(m.nome).includes(termo));
    },
    municipiosValidos() {
      return !this.receberMunicipais || this.todos || this.selecionados.length > 0;
    },
    // Estadual deixou de ser obrigatório: basta acompanhar algum ente.
    valido() {
      return (this.receberEstadual || this.receberMunicipais) && this.municipiosValidos;
    }
  },
  watch: {
    receberMunicipais(ativo) {
      if (!ativo) this.limparMunicipios();
      this.emitir();
    },
    receberEstadual: "emitir",
    todos: "emitir",
    selecionados: "emitir",
    // O pai pode trocar o v-model por fora (ex.: limpar o formulário após cadastrar).
    // O próprio emitir() também volta por aqui: só rehidrata quando o valor recebido
    // difere do estado local, senão desmarcaria "receber municipais" a cada clique.
    value(novo) {
      if (this.carregando || this.erro || !novo) return;
      if (!this.mesmoEstadoLocal(novo)) this.hidratar(novo);
    }
  },
  created() {
    this.api = new EntesFederativosApi();
    this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      this.erro = false;

      let catalogo;
      try {
        catalogo = await this.api.getEntesAcompanhaveis();
      } catch (err) {
        console.log(err);
        this.erro = true;
        this.carregando = false;
        // Sem catálogo não há seleção a propor: emitir `input` aqui mandaria uma
        // seleção vazia ao pai, que ao salvar apagaria os municípios já escolhidos.
        this.$emit("validade", true);
        return;
      }

      this.municipios = catalogo.municipios || [];
      if (catalogo.estadual && catalogo.estadual.nome) {
        this.nomeEstadual = catalogo.estadual.nome;
      }

      this.carregando = false;
      this.hidratar(this.value || {});
      this.emitir();
    },
    // "Águas Lindas" precisa casar com a busca por "aguas"
    normalizar(texto) {
      return String(texto)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    // Cadastro anterior à opção não traz receberEstadual: ele sempre recebeu.
    hidratar({ receberEstadual, todosMunicipios, entesFederativosIds }) {
      this.receberEstadual = receberEstadual !== false;
      this.todos = !!todosMunicipios;
      this.selecionados = entesFederativosIds ? [...entesFederativosIds] : [];
      this.receberMunicipais = this.todos || this.selecionados.length > 0;
    },
    mesmoEstadoLocal({ receberEstadual, todosMunicipios, entesFederativosIds }) {
      if ((receberEstadual !== false) !== this.receberEstadual) return false;

      const todos = !!todosMunicipios;
      if (todos !== this.todos) return false;
      if (todos) return true;

      const ids = entesFederativosIds || [];
      return ids.length === this.selecionados.length &&
        ids.every(id => this.selecionados.includes(id));
    },
    // Só reatribui quando há de fato o que limpar: `this.selecionados = []`
    // incondicional cria um array novo a cada chamada e realimenta o watcher.
    limparMunicipios() {
      if (this.todos) this.todos = false;
      if (this.selecionados.length > 0) this.selecionados = [];
    },
    emitir() {
      // Os watchers disparam durante a hidratação; antes do catálogo chegar
      // (ou se ele falhou) o estado local não reflete o que o pai tem.
      if (this.carregando || this.erro) return;

      this.$emit("input", {
        receberEstadual: this.receberEstadual,
        todosMunicipios: this.todos,
        entesFederativosIds: this.todos ? [] : this.selecionados
      });
      this.$emit("validade", this.valido);
    }
  }
};
</script>

<style lang="scss" scoped>
.entes {
  border: solid 0.05rem #083e07;
  border-radius: 0.3rem;
  padding: 0.75rem 1rem 1rem;
  margin-top: 1.5rem;

  legend {
    font-size: 0.9rem;
    font-weight: bold;
    color: #115810;
    padding: 0 0.4rem;
  }
}

.carregando,
.erro {
  font-size: 0.9rem;
  margin: 0.25rem 0;
}

.erro {
  color: rgb(192, 0, 0);
}

.opcao {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
  cursor: pointer;

  input {
    margin-top: 0.25rem;
    flex: 0 0 auto;
  }

  small {
    display: block;
    font-size: 0.75rem;
    color: #555;
  }
}

.municipios {
  margin-top: 0.75rem;
  padding-left: 1.5rem;
  border-left: solid 0.15rem #ffb24e;
}

.busca {
  width: calc(100% - 1rem);
  height: 2rem;
  border-radius: 0.2rem;
  border: #083e07 solid 0.05rem;
  padding-left: 0.5rem;
  font-family: "Poppins";
  font-size: 1rem;
  margin: 0.5rem 0;
}

.contador {
  font-size: 0.8rem;
  margin: 0.25rem 0;

  button {
    background: none;
    border: none;
    color: #115810;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.8rem;
  }
}

.lista {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
  max-height: 15rem;
  overflow-y: auto;

  li {
    margin: 0;
  }
}

.aviso {
  font-size: 0.85rem;
  color: rgb(192, 0, 0);
  margin: 0.5rem 0 0;
}

.alto-contraste {
  .entes {
    legend {
      color: #ffa900;
    }

    .opcao small {
      color: #ddd;
    }
  }
}
</style>
